import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import html2canvas from "html2canvas";
import { labNow } from "../../utils/labClock";

var MARGIN = 14;
var PAGE_W = 210;
var PAGE_H = 297;
var CONTENT_W = PAGE_W - MARGIN * 2;
var FOOTER_Y = PAGE_H - 8;

// Capture a chart div as a PNG data URL via html2canvas.
// Returns null on failure so callers can fall back to a placeholder.
function captureChartImage(ref) {
  if (!ref || !ref.current) return Promise.resolve(null);
  var el = ref.current;
  return html2canvas(el, {
    scale: 2,
    backgroundColor: "#ffffff",
    useCORS: true,
    logging: false,
  })
    .then(function (canvas) {
      return canvas.toDataURL("image/png");
    })
    .catch(function () {
      return null;
    });
}

function addFooter(doc, pageNum, totalPages, labName) {
  doc.setFontSize(8);
  doc.setTextColor(120);
  doc.text(labName, MARGIN, FOOTER_Y);
  doc.text("Page " + pageNum + " of " + totalPages, PAGE_W / 2, FOOTER_Y, {
    align: "center",
  });
  doc.text("Generated by OpenELIS Global", PAGE_W - MARGIN, FOOTER_Y, {
    align: "right",
  });
  doc.setTextColor(0);
}

function addChartOrPlaceholder(doc, imgData, y, h) {
  if (imgData) {
    doc.addImage(imgData, "PNG", MARGIN, y, CONTENT_W, h);
  } else {
    doc.setFontSize(9);
    doc.setTextColor(150);
    doc.text("[Chart image unavailable]", PAGE_W / 2, y + h / 2, {
      align: "center",
    });
    doc.setTextColor(0);
  }
}

export function generateCompliancePdf(data, meta) {
  var summary = data.summary;
  var siteParams = data.siteParams;
  var exceedances = data.exceedances;
  var labName = meta.labName || "OpenELIS Lab";
  var period = meta.period || "";
  var standard = meta.standard || "All Standards";
  var siteName = meta.siteName || "";
  var preparedBy = meta.preparedBy || "";
  var trendRef = meta.trendRef;
  var drilldownRef = meta.drilldownRef;
  var compRef = meta.comparisonRef;
  var totalPages = 5;
  var generated = meta.generatedAt || labNow().toLocaleString();

  return Promise.all([
    captureChartImage(trendRef),
    captureChartImage(drilldownRef),
    captureChartImage(compRef),
  ]).then(function (imgs) {
    var trendImg = imgs[0];
    var drilldownImg = imgs[1];
    var comparisonImg = imgs[2];

    var doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });

    // ── Page 1: Cover ───────────────────────────────────────────────────────
    doc.setFontSize(10);
    doc.setTextColor(80);
    doc.text(labName.toUpperCase(), PAGE_W / 2, 50, { align: "center" });
    doc.setFontSize(20);
    doc.setTextColor(0);
    doc.setFont("helvetica", "bold");
    doc.text("Environmental Compliance Dashboard", PAGE_W / 2, 68, {
      align: "center",
      maxWidth: CONTENT_W,
    });
    doc.setFont("helvetica", "normal");
    doc.setDrawColor(0, 98, 196);
    doc.setLineWidth(0.5);
    doc.line(MARGIN + 20, 74, PAGE_W - MARGIN - 20, 74);
    var coverFields = siteName
      ? [
          ["Site:", siteName],
          ["Period:", period],
          ["Standard:", standard],
          ["Prepared by:", preparedBy],
          ["Generated:", generated],
        ]
      : [
          ["Period:", period],
          ["Standard:", standard],
          ["Prepared by:", preparedBy],
          ["Generated:", generated],
        ];
    var y = 88;
    doc.setFontSize(11);
    coverFields.forEach(function (f) {
      doc.setFont("helvetica", "bold");
      doc.text(f[0], PAGE_W / 2 - 28, y, { align: "right" });
      doc.setFont("helvetica", "normal");
      doc.text(f[1] || "—", PAGE_W / 2 - 22, y);
      y += 10;
    });
    // KPI summary block
    if (summary) {
      var kpis = [
        ["Total Orders", String(summary.totalOrders ?? "—")],
        [
          "Compliance Rate",
          summary.complianceRate != null ? summary.complianceRate + "%" : "—",
        ],
        ["Total Exceedances", String(summary.totalExceedances ?? "—")],
        ["Sites Monitored", String(summary.sitesMonitored ?? "—")],
      ];
      var kpiY = y + 8;
      var kpiW = CONTENT_W / kpis.length;
      doc.setDrawColor(220);
      doc.setLineWidth(0.3);
      doc.rect(MARGIN, kpiY - 6, CONTENT_W, 22);
      kpis.forEach(function (kpi, i) {
        var kx = MARGIN + kpiW * i + kpiW / 2;
        doc.setFontSize(8);
        doc.setTextColor(100);
        doc.text(kpi[0], kx, kpiY, { align: "center" });
        doc.setFontSize(16);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(0);
        doc.text(kpi[1], kx, kpiY + 10, { align: "center" });
        doc.setFont("helvetica", "normal");
      });
    }
    addFooter(doc, 1, totalPages, labName);

    // ── Page 2: Trend chart ──────────────────────────────────────────────────
    doc.addPage();
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Compliance Rate Trend — All Sites", PAGE_W / 2, 20, {
      align: "center",
    });
    doc.setFont("helvetica", "normal");
    addChartOrPlaceholder(doc, trendImg, 28, 110);
    addFooter(doc, 2, totalPages, labName);

    // ── Page 3: Site drill-down ──────────────────────────────────────────────
    doc.addPage();
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    var drillTitle =
      siteParams && siteParams.siteName
        ? "Site Drill-Down — " + siteParams.siteName
        : "Site Drill-Down — Per-Parameter Trend";
    doc.text(drillTitle, PAGE_W / 2, 20, {
      align: "center",
      maxWidth: CONTENT_W,
    });
    doc.setFont("helvetica", "normal");
    addChartOrPlaceholder(doc, drilldownImg, 28, 110);
    addFooter(doc, 3, totalPages, labName);

    // ── Page 4: Site comparison ──────────────────────────────────────────────
    doc.addPage();
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Site Compliance Comparison — " + period, PAGE_W / 2, 20, {
      align: "center",
      maxWidth: CONTENT_W,
    });
    doc.setFont("helvetica", "normal");
    addChartOrPlaceholder(doc, comparisonImg, 28, 110);
    addFooter(doc, 4, totalPages, labName);

    // ── Page 5: Exceedances table ────────────────────────────────────────────
    doc.addPage();
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Exceedance Summary", PAGE_W / 2, 20, { align: "center" });
    doc.setFont("helvetica", "normal");
    var excRows = (
      exceedances && exceedances.items ? exceedances.items : []
    ).map(function (item) {
      return [
        item.date,
        item.labNumber,
        item.siteName || "—",
        item.parameter,
        item.result,
        item.threshold,
        item.status,
      ];
    });
    autoTable(doc, {
      startY: 28,
      head: [
        ["Date", "Lab #", "Site", "Parameter", "Result", "Threshold", "Status"],
      ],
      body: excRows,
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [0, 98, 196], textColor: 255 },
      columnStyles: { 6: { cellWidth: 20 } },
      didParseCell: function (d) {
        if (d.section === "body" && d.column.index === 6) {
          if (d.cell.raw === "FAIL") {
            d.cell.styles.textColor = [218, 30, 40];
            d.cell.styles.fontStyle = "bold";
          }
          if (d.cell.raw === "MARGINAL") {
            d.cell.styles.textColor = [255, 131, 0];
            d.cell.styles.fontStyle = "bold";
          }
        }
      },
      didDrawPage: function (hookData) {
        var pn = 4 + hookData.pageNumber;
        addFooter(doc, pn, Math.max(totalPages, pn), labName);
      },
    });

    var filename =
      "compliance-dashboard-" + period.replace(/[^0-9-]/g, "_") + ".pdf";
    doc.save(filename);
  });
}
